import { useEffect, useRef } from "react";

// Animated deep-space canvas: drifting stars, twinkle, connecting constellations,
// and floating gold particles. Lightweight, dependency-free.
export const StarfieldBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let w, h, dpr;
    let stars = [];
    let particles = [];
    let raf;

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const count = Math.min(160, Math.floor((w * h) / 9000));
      stars = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        r: Math.random() * 1.4 + 0.3,
        vx: (Math.random() - 0.5) * 0.08,
        vy: (Math.random() - 0.5) * 0.08,
        tw: Math.random() * Math.PI * 2,
        tws: Math.random() * 0.02 + 0.005,
      }));
      particles = Array.from({ length: 18 }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        r: Math.random() * 2 + 1,
        vy: -(Math.random() * 0.25 + 0.05),
        vx: (Math.random() - 0.5) * 0.1,
        a: Math.random() * 0.5 + 0.2,
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);

      // constellation lines
      for (let i = 0; i < stars.length; i++) {
        const s = stars[i];
        for (let j = i + 1; j < stars.length; j++) {
          const o = stars[j];
          const dx = s.x - o.x;
          const dy = s.y - o.y;
          const dist = dx * dx + dy * dy;
          if (dist < 13000) {
            const alpha = (1 - dist / 13000) * 0.12;
            ctx.strokeStyle = `rgba(139,92,246,${alpha})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(s.x, s.y);
            ctx.lineTo(o.x, o.y);
            ctx.stroke();
          }
        }
      }

      // stars
      stars.forEach((s) => {
        s.x += s.vx;
        s.y += s.vy;
        s.tw += s.tws;
        if (s.x < 0) s.x = w;
        if (s.x > w) s.x = 0;
        if (s.y < 0) s.y = h;
        if (s.y > h) s.y = 0;
        const twinkle = 0.55 + Math.sin(s.tw) * 0.45;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${twinkle})`;
        ctx.fill();
      });

      // gold floating particles
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.y < -10) {
          p.y = h + 10;
          p.x = Math.random() * w;
        }
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(212,175,55,${p.a})`;
        ctx.shadowBlur = 8;
        ctx.shadowColor = "rgba(212,175,55,0.6)";
        ctx.fill();
        ctx.shadowBlur = 0;
      });

      raf = requestAnimationFrame(draw);
    };

    resize();
    draw();
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <div className="fixed inset-0 -z-10 overflow-hidden bg-[#05050A]" aria-hidden="true">
      <div className="nebula-blob" style={{ width: 500, height: 500, top: "-10%", left: "-5%", background: "#4C1D95" }} />
      <div className="nebula-blob" style={{ width: 460, height: 460, bottom: "-15%", right: "-8%", background: "#155E75" }} />
      <div className="nebula-blob" style={{ width: 380, height: 380, top: "40%", left: "55%", background: "#7C3AED", opacity: 0.22 }} />
      <canvas ref={canvasRef} className="absolute inset-0" />
    </div>
  );
};

export default StarfieldBackground;
